import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/data/DataTable'
import { useRequestApprovals, useApproveRequestApproval, useDeleteRequestApproval } from '../hooks'
import { createRequestApprovalColumns } from '../columns'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { debounce } from '@/lib/utils'
import type { RequestApproval } from '../api'
import type { ListParams } from '@/lib/api/types'
import { FileCheck } from 'lucide-react'
import { RequestApprovalApproveDialog } from '../components/RequestApprovalApproveDialog'
import { RequestApprovalDeleteDialog } from '../components/RequestApprovalDeleteDialog'
import { UnassignConfirmDialog } from '@/features/my-assignments/components/UnassignConfirmDialog'
import { useUnassignEngineer } from '@/features/recover-requests/hooks'

export function RequestApprovalListPage() {
    const { t, i18n } = useTranslation()
    const [selectedRequestApproval, setSelectedRequestApproval] = useState<RequestApproval | null>(null)
    const [showApproveDialog, setShowApproveDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [unassignDialog, setUnassignDialog] = useState<{ objectID: string; objectDescription: string | null; recoverRequestId: number | null } | null>(null)
    const approveRequestApproval = useApproveRequestApproval()
    const deleteRequestApproval = useDeleteRequestApproval()
    const unassign = useUnassignEngineer()
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')

    // Debounce search so we don't refetch on every keystroke
    const debouncedSetSearch = useMemo(() => debounce((value: string) => {
        setDebouncedSearch(value)
        setPage(1) // Reset to page 1 when search changes
    }, 350), [])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    // Memoize params to prevent unnecessary re-renders
    const params: ListParams = useMemo(() => ({
        page,
        perPage: limit,
        search: debouncedSearch || undefined
    }), [page, limit, debouncedSearch])

    const { data: requestApprovalsResponse, isLoading } = useRequestApprovals(params)

    // Memoize data extraction to prevent unnecessary recalculations
    const apiResponse = useMemo(() => requestApprovalsResponse?.data, [requestApprovalsResponse])
    const requestApprovals = useMemo(() => apiResponse?.data || [], [apiResponse])
    const totalRequestApprovals = useMemo(() => 
        apiResponse?.pagination?.total || apiResponse?.total || 0, 
        [apiResponse]
    )

    // Memoize callbacks to prevent re-creating on every render
    const handleApprove = useCallback((requestApproval: RequestApproval) => {
        setSelectedRequestApproval(requestApproval)
        setShowApproveDialog(true)
    }, [])

    const handleApproveConfirm = useCallback(() => {
        if (selectedRequestApproval?.requestID) {
            approveRequestApproval.mutate(selectedRequestApproval.requestID, {
                onSuccess: () => {
                    setShowApproveDialog(false)
                    setSelectedRequestApproval(null)
                }
            })
        }
    }, [selectedRequestApproval, approveRequestApproval])

    const handleDelete = useCallback((requestApproval: RequestApproval) => {
        setSelectedRequestApproval(requestApproval)
        setShowDeleteDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedRequestApproval?.requestID) {
            deleteRequestApproval.mutate(selectedRequestApproval.requestID, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedRequestApproval(null)
                },
            })
        }
    }, [selectedRequestApproval, deleteRequestApproval])

    const handleUnassign = useCallback((requestApproval: RequestApproval) => {
        if (!requestApproval.requestID || !requestApproval.objectID) return
        const requestIdNum = Number(requestApproval.requestID)
        if (isNaN(requestIdNum)) return
        setUnassignDialog({
            objectID: requestApproval.objectID,
            objectDescription: requestApproval.objectDescription,
            recoverRequestId: requestIdNum,
        })
    }, [])

    const handleConfirmUnassign = useCallback(() => {
        if (!unassignDialog || unassignDialog.recoverRequestId == null) return
        unassign.mutate(
            { requestID: unassignDialog.recoverRequestId },
            {
                onSettled: () => setUnassignDialog(null),
            }
        )
    }, [unassignDialog, unassign])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export request approvals to CSV')
    }, [])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createRequestApprovalColumns(handleApprove, handleDelete, handleUnassign, t, i18n.language),
        [handleApprove, handleDelete, handleUnassign, t, i18n.language]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('requestApprovals.loading')}</div>
            </div>
        )
    }

    return (
        <AdminRoute>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('requestApprovals.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('requestApprovals.description')}
                        </p>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-lg border bg-card p-6 md:col-start-2">
                        <div className="flex items-center space-x-2">
                            <FileCheck className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('requestApprovals.totalRequestApprovals')}</p>
                                <p className="text-2xl font-bold">{totalRequestApprovals}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table - Narrower Width */}
                <div 
                    className="overflow-x-auto" 
                    style={{ 
                        maxWidth: '1200px',
                        margin: '0 auto'
                    }}
                >
                    <DataTable
                        columns={columns}
                        data={requestApprovals}
                        searchKey="objectID"
                        searchPlaceholder={t('requestApprovals.searchPlaceholder')}
                        searchValue={search}
                        onSearchChange={setSearch}
                        onExport={handleExport}
                        exportButtonText={t('requestApprovals.export')}
                        enableRowSelection={false}
                        pagination={{
                            page,
                            limit,
                            total: totalRequestApprovals,
                            onPageChange: setPage,
                            onPageSizeChange: setLimit,
                        }}
                    />
                </div>

                {/* Dialogs */}
                <RequestApprovalApproveDialog
                    open={showApproveDialog}
                    onOpenChange={setShowApproveDialog}
                    requestApproval={selectedRequestApproval}
                    onConfirm={handleApproveConfirm}
                    isLoading={approveRequestApproval.isPending}
                />

                <RequestApprovalDeleteDialog
                    open={showDeleteDialog}
                    onOpenChange={setShowDeleteDialog}
                    requestApproval={selectedRequestApproval}
                    onConfirm={handleDeleteConfirm}
                    isLoading={deleteRequestApproval.isPending}
                />

                {/* Unassign Confirm Dialog */}
                {unassignDialog && (
                    <UnassignConfirmDialog
                        open={!!unassignDialog}
                        onOpenChange={(open) => {
                            if (!open) setUnassignDialog(null)
                        }}
                        objectID={unassignDialog.objectID}
                        objectDescription={unassignDialog.objectDescription}
                        recoverRequestId={unassignDialog.recoverRequestId}
                        onConfirm={handleConfirmUnassign}
                        isLoading={unassign.isPending}
                    />
                )}
            </div>
        </AdminRoute>
    )
}
